import { useState } from 'react';
import config from 'config';
import { useShowSvcPrice } from 'context/ShowSvcPriceContext';
import Image from 'next/image';
import { returnDate } from 'services';

import {
  Flex,
  Grid,
  GridItem,
  Stack,
  Text,
  useBoolean,
  useTheme
} from '@chakra-ui/react';

import { convertDuration, formatCurrency } from 'utils';

import { AppointmentService, ServicePriceInterface } from 'types';

import { Divider, Loader } from 'ui';

const {
  booking: { DefaultProviderAvatar }
} = config;

interface ReservationItemProps {
  item: AppointmentService;
  showDivider: boolean;
  dataPrice: ServicePriceInterface | undefined;
}

const ReservationItem = ({
  item,
  dataPrice,
  showDivider
}: ReservationItemProps) => {
  const showSvcPrice = useShowSvcPrice();
  const {
    colors: {
      palette: {
        booking: {
          stepReview: {
            reservationList: { reservationItem }
          }
        }
      }
    }
  } = useTheme();
  const [imageError, setImageError] = useState<boolean>(false);
  const [loading, setLoading] = useBoolean(true);

  if (!item) return null;

  const {
    svc_start_time,
    svc_end_time,
    public_photo_url,
    em_name,
    assigned_em_2,
    svc_name
  } = item;

  const price = dataPrice?.price || item.price;
  const price_orig = dataPrice?.price_orig || item.price_orig;
  const formattedStartTime = returnDate(svc_start_time, 'HH:mm:ss');
  const formattedEndTime = returnDate(svc_end_time, 'HH:mm:ss');
  const durationHours = formattedEndTime.diff(formattedStartTime, 'hour');
  const durationMinutes =
    formattedEndTime.diff(formattedStartTime, 'minute') % 60;
  const duration = `${durationHours}:${durationMinutes}`;

  const providerDescription = assigned_em_2?.[0]?.em_title ?? '';
  const hasClientDiscount = price_orig > 0 && price_orig > price;
  const discount = hasClientDiscount ? price_orig - price : 0;
  const providerAvatar = public_photo_url?.[0] ?? '';

  return (
    <Grid
      position='relative'
      templateColumns='repeat(12, 1fr)'
      templateRows={{
        base: 'repeat(2, auto)',
        lg: 'repeat(1, 1fr)'
      }}
      mt={{ base: 0, lg: '6px' }}
      px={{ base: '15px', md: '30px' }}
      py={{ base: '15px', md: '23px' }}
      alignItems='center'
    >
      <GridItem
        colSpan={{ base: 8, lg: 6 }}
        rowSpan={1}
        gridRowStart={1}
        pb='15px'
      >
        <Text
          color={reservationItem.serviceNameColor}
          as='span'
          fontSize={{
            base: '16px',
            sm: '18px',
            md: '20px',
            xl: '25px'
          }}
          mt={{ base: '-3px', lg: 0 }}
          mr='10px'
        >
          {svc_name}
        </Text>
      </GridItem>
      <GridItem colSpan={{ base: 12, lg: 4 }} rowSpan={{ base: 1, lg: 1 }}>
        <Flex alignItems='center' gap='10px'>
          <Flex
            w={{ base: '35px', sm: '40px' }}
            h={{ base: '35px', sm: '40px' }}
            position='relative'
            overflow='hidden'
            alignItems='center'
            justifyContent='center'
            borderRadius='50%'
            flexShrink={0}
          >
            {providerAvatar?.includes('http') && !imageError ? (
              <>
                {loading && <Loader absolute />}
                <Image
                  sizes='(max-width: 450px) 40px, 65px'
                  src={providerAvatar}
                  alt='Appointment'
                  fill
                  onError={() => setImageError(true)}
                  onLoad={setLoading.off}
                  style={{ objectFit: 'cover', objectPosition: 'top' }}
                />
              </>
            ) : (
              <DefaultProviderAvatar
                width={65}
                height={65}
                fill={reservationItem.iconFill}
                style={{ opacity: 0.5 }}
              />
            )}
          </Flex>
          <Stack gap='6px' justifyContent='center' height='100%'>
            <Text
              color={reservationItem.providerNameColor}
              fontSize={{ base: '14px', md: '16px' }}
              textTransform='uppercase'
            >
              {em_name?.[0] ?? ''}
            </Text>

            {providerDescription && (
              <Text
                color={reservationItem.providerDescriptionColor}
                fontSize='12px'
              >
                {providerDescription}
              </Text>
            )}
          </Stack>
        </Flex>
      </GridItem>
      <GridItem
        colSpan={{ base: 4, lg: 2 }}
        rowSpan={{ base: 1, lg: 1 }}
        gridRowStart={1}
        height='100%'
      >
        <Stack alignItems='flex-end' gap='0' ml='20px' height='100%'>
          {showSvcPrice && (
            <Flex
              alignItems='center'
              gap={{ base: '22px', md: '18px' }}
              height='100%'
            >
              {hasClientDiscount && (
                <Text
                  position='relative'
                  color={reservationItem.discountColor}
                  fontSize={{ base: '12px', md: '14px' }}
                  _after={{
                    position: 'absolute',
                    content: '""',
                    display: 'block',
                    width: 'calc(100% + 1px)',
                    height: '1px',
                    bg: reservationItem.discountColor,
                    top: '56%',
                    left: '1px'
                  }}
                >
                  {formatCurrency(price_orig)}
                </Text>
              )}
              <Text
                color={reservationItem.priceColor}
                fontSize={{ base: '16px', sm: '18px', md: '20px', xl: '25px' }}
              >
                {formatCurrency(price)}
              </Text>
            </Flex>
          )}

          <Stack gap='8px' w='100%' alignItems='flex-end' height='100%'>
            {showSvcPrice && hasClientDiscount && (
              <Text
                color={reservationItem.memberSaveColor}
                fontSize={{ base: '13px', md: '15px' }}
                w='100%'
                textAlign='end'
                whiteSpace={{ base: 'normal', md: 'nowrap' }}
              >
                Member&apos;s save {formatCurrency(discount)}
              </Text>
            )}
            <Text
              color={reservationItem.durationColor}
              fontSize={{ base: '12px', md: '15px' }}
            >
              {convertDuration(duration)}
            </Text>
          </Stack>
        </Stack>
      </GridItem>

      {showDivider && (
        <Divider
          position='absolute'
          width='100%'
          border='none'
          bottom='0'
          left='0'
          h='1px'
          zIndex={2}
          bg={reservationItem.dividerColor}
        />
      )}
    </Grid>
  );
};

export default ReservationItem;
